function upsertMetaByName(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string | null): void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!content) {
    existing?.remove();
    return;
  }
  let el = existing;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setDocumentTitle(title: string): void {
  document.title = title;
}

export function setMetaDescription(content: string): void {
  upsertMetaByName("description", content);
}

export function setRobotsMeta(content: string): void {
  upsertMetaByName("robots", content);
}

export function setCanonicalUrl(href: string | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!href) {
    existing?.remove();
    return;
  }
  let el = existing;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const JSON_LD_SCRIPT_ID = "structured-data";

export function setOpenGraph(input: {
  type: string;
  title: string;
  description: string;
  url: string | null;
  image: string | null;
}): void {
  upsertMetaByProperty("og:type", input.type);
  upsertMetaByProperty("og:title", input.title);
  upsertMetaByProperty("og:description", input.description);
  upsertMetaByProperty("og:url", input.url);
  upsertMetaByProperty("og:image", input.image);
}

export function setTwitterCard(input: {
  title: string;
  description: string;
  image: string | null;
}): void {
  upsertMetaByName("twitter:card", input.image ? "summary_large_image" : "summary");
  upsertMetaByName("twitter:title", input.title);
  upsertMetaByName("twitter:description", input.description);
  if (input.image) {
    upsertMetaByName("twitter:image", input.image);
  } else {
    document.head.querySelector('meta[name="twitter:image"]')?.remove();
  }
}

export function setJsonLd(data: Record<string, unknown> | null): void {
  const existing = document.getElementById(JSON_LD_SCRIPT_ID);
  if (!data) {
    existing?.remove();
    return;
  }
  let el = existing as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = JSON_LD_SCRIPT_ID;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applyDocumentSeo(input: {
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: string;
  ogType?: string;
  image?: string | null;
  jsonLd?: Record<string, unknown> | null;
}): void {
  setDocumentTitle(input.title);
  setMetaDescription(input.description);
  setCanonicalUrl(input.canonicalUrl);
  setRobotsMeta(input.robots);
  setOpenGraph({
    type: input.ogType ?? "website",
    title: input.title,
    description: input.description,
    url: input.canonicalUrl,
    image: input.image ?? null,
  });
  setTwitterCard({
    title: input.title,
    description: input.description,
    image: input.image ?? null,
  });
  if (input.jsonLd !== undefined) {
    setJsonLd(input.jsonLd);
  }
}
